import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Button, Space, Steps, Form } from 'antd';
import PropTypes from 'prop-types';
import BreadCrumbs from '../../../components/core/BreadCrumbs';
import { APP_TERMS } from '../../../app-terms';
import PatientDetails from './PatientDetails';
import StudyPurpose from './StudyPurpose';
import AddServices from './AddService';
import UploadFiles from './UploadFiles';
import { useServicesList } from '../../../hooks/services/useServicesList';
import { useNavigate } from 'react-router-dom';
import PaypalPaymentButton from '../../../components/case/PaypalPaymentButton';
import PaypalPaymentButtonSubmitCase from '../../../components/case/PaypalPaymentButtonSubmitCase';
import { PaymentFor } from '../../../appContants';
 import { PayPalScriptProvider } from "@paypal/react-paypal-js";
 import { Environment } from '../../../utils/Environment';
 const PAYPAL_CLIENT_ID = Environment?.paypalClientId();

const FormLayout = forwardRef(function FormLayout(
  {
    patientDetailsForm,
    studyPurposeForm,
    addServiceForm,
    uploadFilesForm,
    loading,
    breadCrumbs,
    onMarkAsSubmitted,
    caseId,
    handleSubmit,
  },
  ref
) {
  const [submittable, setSubmittable] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitCaseInProgress, setSubmitCaseInProgress] = useState(false);
  const addServiceValues = Form.useWatch([], addServiceForm);
  const uploadFileValues = Form.useWatch([], uploadFilesForm);
  const { data: servicesList, getServicesList } = useServicesList();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const goToPrevTab = () => {
    if (activeTab === 0) {
      navigate(-1);
    }
    setActiveTab((value) => value - 1);
  };
  const goToNextTab = () => setActiveTab((value) => value + 1);


  const handlMarkAsSubmitted = async () => {
    onMarkAsSubmitted({ files: uploadFilesForm?.getFieldValue('files') });
  };

  useEffect(() => {
    // Fetch services list
    getServicesList();
  }, []);

  // Handle disabled status of submit call to actions
  useEffect(() => {
    uploadFilesForm
      ?.validateFields({ validateOnly: true })
      .then(() => {
        setSubmittable(true);
      })
      .catch(() => {
        setSubmittable(false);
      });
  }, [uploadFilesForm, uploadFileValues, addServiceValues]);

  useImperativeHandle(ref, () => ({
    goToNextTab,
  }));

  const handleUploading = (uploading) => {
    setUploading(uploading);
  }

  return (
    <div>
      <div>
        <BreadCrumbs items={breadCrumbs} />
        <div className="row card">
          <div className="card invoices-tabs-card">
            <div className="card-body card-body pt-0 pb-0">
              <div className="invoices-main-tabs">
                <div className="row align-items-center">
                  <div className="wizard mt-4">
                    <ul
                      className="nav nav-tabs justify-content-center"
                      role="tablist"
                    >
                      <Steps
                        current={activeTab}
                        items={[
                          {
                            title: `${APP_TERMS.generic_patients} ${APP_TERMS.generic_details}`,
                            icon: <i className="far fa-user"></i>,
                          },
                          {
                            title: `${APP_TERMS.generic_study} ${APP_TERMS.generic_purpose}`,
                            icon: <i className="fa-solid fa-house-medical-circle-check"></i>,
                          },
                          {
                            title: `${APP_TERMS.generic_add} ${APP_TERMS.generic_services}`,
                            icon: <i className="fa fa-bars"></i>,
                          },
                          {
                            title: `${APP_TERMS.generic_upload} ${APP_TERMS.generic_files}`,
                            icon: <i className="fa fa-file"></i>,
                          },
                        ]}
                      />
                    </ul>
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                {activeTab == 0 && <PatientDetails form={patientDetailsForm} />}
                {activeTab == 1 && <StudyPurpose form={studyPurposeForm} />}
                {activeTab == 2 && (
                  <AddServices
                    form={addServiceForm}
                    servicesList={servicesList}
                  />
                )}
                {activeTab == 3 && <UploadFiles form={uploadFilesForm} onUploading={handleUploading} />}
              </div>

              <Space className="mt-3">
                <Button
                  htmlType="button"
                  disabled={!!loading || uploading || submitCaseInProgress}
                  onClick={goToPrevTab}
                >
                  Back
                </Button>
                {activeTab === 3 ? (
                  <>
                    <Button
                      loading={loading === 'saving'}
                      type="primary"
                      htmlType="button"
                      onClick={() => handleSubmit(activeTab)}
                      disabled={!submittable || !!loading || uploading || submitCaseInProgress}
                    >
                      Save Draft
                    </Button>
                    {/* Show PayPal submit button only when not uploading */}
                    {!caseId && submittable && !uploading && (
                      <PayPalScriptProvider
                        options={{
                          "client-id": PAYPAL_CLIENT_ID,
                          components: "buttons",
                          currency: "USD",
                          disableFunding: "credit,card,paylater"
                        }}
                      >
                    <PaypalPaymentButtonSubmitCase
                      handleSubmit={handleSubmit}
                      activeTab={activeTab}
                      paymentFor={PaymentFor.CASE}
                      onSubmitCaseLoadingChange={setSubmitCaseInProgress}
                      parentBusy={!!loading || uploading}
                    /></PayPalScriptProvider>)
                    }
                    {caseId ? (
                      <PaypalPaymentButton
                        paymentFor={PaymentFor.CASE}
                        recordId={caseId}
                        loading={loading === 'submitting'}
                        type="primary"
                        htmlType="button"
                        onMarkAsSubmitted={handlMarkAsSubmitted}
                        disabled={!submittable || !!loading || uploading || submitCaseInProgress}
                        title={'Submit'}
                      />
                    ) : null}
                  </>
                ) : (
                  <Button
                    type="primary"
                    htmlType="button"
                    disabled={!!loading || uploading || submitCaseInProgress}
                    onClick={() => handleSubmit(activeTab)}
                  >
                    Next
                  </Button>  
                )}
                 <Button
                    htmlType="button"
                    disabled={!!loading || uploading || submitCaseInProgress}
                    onClick={() => navigate('/user/case/created')}
                  >
                    Cancel
                </Button>
              </Space>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

FormLayout.propTypes = {
  patientDetailsForm: PropTypes.any,
  studyPurposeForm: PropTypes.any,
  addServiceForm: PropTypes.any,
  uploadFilesForm: PropTypes.any,
  loading: PropTypes.any,
  breadCrumbs: PropTypes.array,
  onMarkAsSubmitted: PropTypes.func,
  caseId: PropTypes.number,
  activeTab:PropTypes.number,
  handleSubmit: PropTypes.func,
};

export default FormLayout;
